import { useEffect, useRef, useState, type FC } from 'react';
import { useDispatch } from 'react-redux';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import type { Animal } from '@/model/Animal.model';
import type { PlannedAnimalDefinition } from './PlannedAnimals/PlannedAnimals.utils';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import type { AnimalMapV1 } from '@/model/PlannedAnimal.model';
import type { StudyApiId } from '@/model/Study.model';

import { errorToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTableV16';

import Button from '@/components/UI/Button';
import Loading from '@/components/Loading';
import Switch from '@/components/UI/FormElements/Switch';
import ApiErrorBanner from '@/components/ApiErrorBanner';

import { type MapAnimalRow, transformAnimalsToMapData, createAnimalMappings } from './MapAnimals.utils';
import MapAnimalsTableV16 from './MapAnimalsTableV16';
import './PlannedAnimals.scss';

interface MapAnimalsProps {
  studyApiId: StudyApiId;
  plannedAnimals: Array<PlannedAnimalDefinition>;
  groups: Array<TreatmentGroup>;
  onSubmit: (mappedAnimals: AnimalMapV1) => Promise<void>;
}

const MapAnimals: FC<MapAnimalsProps> = ({ studyApiId, plannedAnimals, groups, onSubmit }) => {
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [apiError, setApiError] = useState<unknown | null>(null);
  const [mapData, setMapData] = useState<Array<MapAnimalRow>>([]);
  const [overrideNames, setOverrideNames] = useState<boolean>(false);
  const [animalsData, setAnimalsData] = useState<Array<Animal>>([]);

  const ref = useRef<HotTableRef | null>(null);
  const dispatch = useDispatch();

  const { invoke: fetchAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: studyApiId },
    query: { perPage: -1, include: ['study_group', 'cage'] },
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'fetch-animals' } },
  });

  useEffect(() => {
    const loadAnimals = async () => {
      try {
        const response = await fetchAnimals();
        if (response.type === 'success') {
          const animals = ((response?.body as unknown as { data: Array<Animal> })?.data ?? []).filter(
            (animal) => !animal.animal_definition_api_id && _isNotEmpty(animal.study_group)
          );
          setAnimalsData(animals);
        }
      } catch (error) {
        setApiError(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnimals();
  }, []);

  useEffect(() => {
    if (_isNotEmpty(animalsData)) {
      const transformedData = transformAnimalsToMapData(animalsData, plannedAnimals);
      setMapData(transformedData);
    }
  }, [animalsData, plannedAnimals]);

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const submitMapping = async () => {
    if (!isInvalid && !isSubmitting && _notNil(onSubmit)) {
      try {
        setIsSubmitting(true);
        const mappedAnimals = createAnimalMappings(mapData);
        if (_isNotEmpty(mappedAnimals)) {
          await onSubmit({
            mappings: mappedAnimals,
            override_names: overrideNames,
          });
        }
        closeModal();
      } catch (err) {
        errorToast('Could not submit mapping request');
        ExceptionHandler.captureException(
          new InVivoError('Could not map planned animals', {
            cause: err,
            slug: 'map-animals-modal-error',
          })
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  if (_isEmpty(animalsData)) {
    return (
      <div
        data-test-component="EmptyAnimals"
        data-test-element="container"
        className="flex flex-wrap overflow-hidden w-50 center ma3 bg-light-gray br2 mt4 mb-2 relative"
      >
        <div className="pa4">
          <div className="text-center">
            {isLoading ? (
              <Loading txt="Loading animals..." />
            ) : (
              <>
                <h3 className="mb2">No animals found</h3>
                <p className="mb3">Registered animals must be assigned to groups before mapping planned animals.</p>
                <Button plain onClick={closeModal}>
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (_isEmpty(plannedAnimals)) {
    return (
      <div className="mapContainer" data-test-component="EmptyPlannedAnimals" data-test-element="container">
        <div className="pa4">
          <div className="text-center">
            <h3 className="mb2">No planned animals found</h3>
            <p className="mb3">There are no planned animals available to map from.</p>
            <Button plain onClick={closeModal}>
              Close
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mapContainer" data-test-component="MapAnimals" data-test-element="container">
      <div className="mapContentWrapper">
        <div className="mapHeaderSection">
          {apiError ? (
            <ApiErrorBanner
              className="mw6 mb4"
              error={apiError}
              title="There was an error fetching animals data"
              text="An error has occurred when fetching animals data, please try again. If this keeps occurring please contact support."
            />
          ) : null}
          <h3 className="normal f4 lh-title">Map animals to planned animals</h3>
          <p className="lh-copy f5 pt2">
            Map existing animals to planned animals by selecting an available planned animal ID. Animals are
            pre-filtered to match group and sex with planned animals.
          </p>
        </div>
        <div className="ph4 pb4 flex justify-between items-center">
          <div className="flex items-center">
            <Switch
              testId="overrideNames"
              value={overrideNames}
              onChange={() =>
                setOverrideNames((prev) => {
                  return !prev;
                })
              }
            />
            <label className="ml2 mb0">Overwrite animal names with planned IDs</label>
          </div>
        </div>
        <div
          className="ow-spreadsheet-styles mapSpreadsheetContainer"
          data-test-component="MapAnimals"
          data-test-element="spreadsheet-container"
        >
          <MapAnimalsTableV16
            mapData={mapData}
            setMapData={setMapData}
            groups={groups}
            plannedAnimals={plannedAnimals}
            innerRef={ref}
            setInvalid={setIsInvalid}
          />
        </div>
      </div>
      <div
        className="ph4 pv3 flex self-end bt b--moon-gray w-100"
        data-test-component="MapAnimals"
        data-test-element="action-container"
      >
        <Button
          onClick={submitMapping}
          testId="save-action"
          disabled={isInvalid || isSubmitting}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Map Animals
        </Button>
        <Button plain className="ml3" onClick={closeModal} testId="cancel-action">
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default MapAnimals;
